import React, { useMemo } from 'react';
import { useSelector, connect } from '@atlassian/jira-react-redux/src/index.tsx';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types.tsx';
import { UnloadPrompt } from '@atlassian/jira-common-components-unload-prompt/src/index.tsx';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useNavigationBlockModal } from './utils.tsx';
import type { DispatchProps, Props } from './types.tsx';
import { mapDispatchToProps } from './command.tsx';
import { navigationBlockModalEntrypoint } from './modal/entrypoint.tsx';
import type { Props as ModalProps } from './modal/types.tsx';

const entryPointParams = {};

/**
 * useNavigationBlockModal blocks in-spa transitions, UnloadPrompt blocks page unloads
 */
const UnsavedChangesNavBlockInner = (props: Props) => {
	const { stopCommitting } = props;
	const shouldModalBeShown = useSelector(
		(state: State) => state.domain.updateJira.commit.inProgress,
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		navigationBlockModalEntrypoint,
		entryPointParams,
	);
	const openModal: EntryPointActions['load'] = entryPointActions.load;
	const closeModal: EntryPointActions['unload'] = entryPointActions.unload;

	const { continueNavigation } = useNavigationBlockModal(shouldModalBeShown, openModal);

	const modalProps: ModalProps = useMemo(
		() => ({
			closeModal,
			continueNavigation: () => {
				if (shouldModalBeShown) {
					// only stop committing if saving is in progress
					stopCommitting();
				}
				continueNavigation();
			},
		}),
		[closeModal, continueNavigation, shouldModalBeShown, stopCommitting],
	);

	return (
		<>
			<UnloadPrompt beforeUnload={shouldModalBeShown} />
			<JiraEntryPointContainer
				id="jira.plans.update-jira.navigation-block-modal"
				packageName="jiraPlansNavigationBlock"
				entryPointReferenceSubject={entryPointReferenceSubject}
				runtimeProps={modalProps}
			/>
		</>
	);
};

export const UnsavedChangesNavBlock = connect<{}, DispatchProps, {}, {}>(
	null,
	mapDispatchToProps,
)(UnsavedChangesNavBlockInner);
